import {Cart, ICartData, Checkout, IAddressData, IGetCheckoutShippingOptionsData} from "../../../../typescript-library";
import * as Sentry from "@sentry/browser";
import i18n from "i18next";
import {isToNorthIreland} from "../../../checkout/steps/AddressStep/AddressStep";
import {positionsToDisplayItems} from "../googlepay/googlePayHelperFunctions";
import ApplePayLineItem = ApplePayJS.ApplePayLineItem;
import ApplePayShippingMethod = ApplePayJS.ApplePayShippingMethod;
import ApplePayDateComponentsRange = ApplePayJS.ApplePayDateComponentsRange;
import ApplePayShippingMethodSelectedEvent = ApplePayJS.ApplePayShippingMethodSelectedEvent;
import ApplePayCouponCodeChangedEvent = ApplePayJS.ApplePayCouponCodeChangedEvent;
import ApplePayCouponCodeUpdate = ApplePayJS.ApplePayCouponCodeUpdate;
import ApplePayPaymentAuthorizedEvent = ApplePayJS.ApplePayPaymentAuthorizedEvent;
import ApplePayShippingContactUpdate = ApplePayJS.ApplePayShippingContactUpdate;
import ApplePayPaymentContact = ApplePayJS.ApplePayPaymentContact;
import ApplePayErrorContactField = ApplePayJS.ApplePayErrorContactField;
import ApplePayErrorCode = ApplePayJS.ApplePayErrorCode;
import {Payments} from "../PaymentController";
import {PaymentStateType} from "./ApplePayButtonLoader";
import {splitStreetAndNumber} from "../paypal/helpers/paypalAddressHelpers";
import {ICartContext} from "../../../cart/CartContextProvider/CartContextProvider";
import {PlaceType} from "../paypal/helpers/paypalTypes";

// Will be deleted on end of debugging and console.log removal!
/* eslint-disable no-console */

/**
 * Maps our shipping to the ones in `ApplePayShippingMethod` format
 * @param shippingOption
 * @param isNet
 */
export function mapShipping(shippingOption: IGetCheckoutShippingOptionsData, isNet: boolean): ApplePayShippingMethod {
  const {deliveryDaysMin, deliveryDaysMax} = shippingOption;

  const now = new Date();
  const firstThen = new Date(now.getFullYear(), now.getMonth(), now.getDate() + deliveryDaysMin);
  const lastThen = new Date(now.getFullYear(), now.getMonth(), now.getDate() + deliveryDaysMax);

  const dateComponentsRange: ApplePayDateComponentsRange = {
    startDateComponents: {
      years: firstThen.getFullYear(),
      months: firstThen.getMonth(),
      days: firstThen.getDate(),
      hours: 0,
    },
    endDateComponents: {
      years: lastThen.getFullYear(),
      months: lastThen.getMonth(),
      days: lastThen.getDate(),
      hours: 0,
    },
  };

  let amount = shippingOption.amount.amount;
  if (isNet) {
    amount = (Math.round(Number(shippingOption.amount.amount) / 0.0119) / 100).toString();
  }

  return {
    label: shippingOption.name,
    detail: shippingOption.description ?? "",
    amount,
    identifier: shippingOption.logisticsTypeId.toString(),
    dateComponentsRange,
  };
}

/**
 * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778009-onshippingcontactselected
 * @param params {cart, checkout, session, shippingMethods}
 */
export async function asyncPutShippingCountry(params: {
  shippingContact: ApplePayPaymentContact;
  cart: Cart | undefined;
  checkout: Checkout | undefined;
  session: ApplePaySession;
  shippingMethods: ApplePayShippingMethod[] | undefined;
}) {
  console.log("shipping country selection started");
  if (!params.checkout) throw new Error("No checkout object in putShippingCountry!");
  if (typeof params.cart === "undefined") throw new Error("No cart object in putShippingCountry!");

  const {countryCode, postalCode} = params.shippingContact;
  if (!countryCode || !postalCode || isToNorthIreland(countryCode, postalCode)) {
    console.log("countryCode or postalCode error:", countryCode || typeof countryCode, postalCode || typeof postalCode);
    params.session.completeShippingContactSelection(errorUpdate(params.cart));

    return;
  }

  let shippingOptions: IGetCheckoutShippingOptionsData[] | undefined;
  const differingCountryCodes = countryCode !== params.cart.countryCode;

  if (differingCountryCodes) {
    await params.cart.put({countryCode});
  }
  if (differingCountryCodes || !params.shippingMethods) {
    shippingOptions = await params.checkout.getAvailableShippingOptions();

    if (!shippingOptions.length) {
      // Maybe same, when there is an item with 'NOT_ALLOWED_FOR_COUNTRY'
      // then you don't need the .getAvailableShippingOptions() call
      console.log(
        "No shippingOptions!! Any item not shippable?:",
        params.cart.positions.find((pos) => pos.messages.some((msg) => msg.code === "NOT_ALLOWED_FOR_COUNTRY"))?.text,
      );
      params.session.completeShippingContactSelection(errorUpdate(params.cart));

      return;
    }
  }

  const update: ApplePayShippingContactUpdate = {
    newTotal: {
      label: i18n.t("cart.total"),
      amount: params.cart.valuePayable.amount,
    },
    newLineItems: positionsToLineItems(params.cart),
  };

  if (shippingOptions) {
    const isNet = params.cart.isNet;
    update.newShippingMethods = shippingOptions.map((shippingOption: IGetCheckoutShippingOptionsData) =>
      mapShipping(shippingOption, isNet),
    );
  } else {
    update.newShippingMethods = params.shippingMethods;
  }

  params.session.completeShippingContactSelection(update);
  console.log("Shipping country selection completed");

  return update.newShippingMethods;
}

/**
 * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778028-onshippingmethodselected
 * @param event
 * @param checkout
 * @param session
 */
export async function asyncPutShippingMethod(
  event: ApplePayShippingMethodSelectedEvent,
  checkout: Checkout | undefined,
  session: ApplePaySession,
) {
  console.log("shipping method selection started");
  if (!checkout) {
    throw new Error("No checkout object in putShippingMethod!");
  }

  await checkout.put({logisticsTypeId: parseInt(event.shippingMethod.identifier, 10)});

  session.completeShippingMethodSelection({
    newTotal: {
      label: i18n.t("cart.total"),
      amount: checkout.cartData.value.payable.amount,
    },
    newLineItems: positionsToLineItems(checkout.cartData),
  });
  console.log("Shipping method selection completed");
}

/**
 * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/1778013-onpaymentmethodselected
 * @param checkout
 * @param session
 */
export async function putPaymentMethod(checkout: Checkout | undefined, session: ApplePaySession) {
  console.log("payment method selection started");
  if (!checkout) {
    throw new Error("No checkout object in putPaymentMethod!");
  }

  if (checkout.paymentId !== Payments.ApplePay) {
    await checkout.put({paymentId: Payments.ApplePay});
  }

  session.completePaymentMethodSelection({
    newTotal: {
      label: i18n.t("cart.total"),
      amount: checkout.cartData.value.payable.amount,
    },
    newLineItems: positionsToLineItems(checkout.cartData),
  });
  console.log("Payment selection completed");
}

/**
 * https://developer.apple.com/documentation/apple_pay_on_the_web/applepaysession/3813982-oncouponcodechanged
 * @param event
 * @param cart
 * @param session
 */
export async function asyncCouponCodeChange(
  event: ApplePayCouponCodeChangedEvent,
  session: ApplePaySession,
  cart: Cart,
) {
  await cart.addVoucher(event.couponCode);

  const vouchers = cart.positions.filter((pos) => pos.type === "Voucher");
  const errors: ApplePayError[] = [];
  vouchers.forEach((voucher) => {
    [...(voucher.reference?.voucher?.messages ?? []), ...voucher.messages].forEach((msg) =>
      errors.push(
        new ApplePayError(
          "couponCodeInvalid",
          undefined,
          i18n.t(msg.code, {
            ns: ["errorcodes", "translations"],
            voucherCode: voucher.reference?.voucherCode,
            minimumOrderValue: voucher.reference?.voucher?.minimumOrderValueAbsolute?.formatted,
          }),
        ),
      ),
    );
  });

  const update: ApplePayCouponCodeUpdate = {
    newLineItems: positionsToLineItems(cart),
    newTotal: {
      label: i18n.t("cart.total"),
      amount: cart.valuePayable.amount,
    },
    errors: errors.length ? errors : undefined,
  };
  session.completeCouponCodeChange(update);
  console.log("Coupon code change completed");
}

export const findAuthorizeErrors = (
  event: ApplePayPaymentAuthorizedEvent,
  paymentData: PaymentStateType | undefined,
) => {
  const {billingContact, shippingContact} = event.payment;

  const errorParamsArray: [ApplePayErrorCode, ApplePayErrorContactField | undefined, string][] = [];
  if (!paymentData) {
    errorParamsArray.push(["unknown", undefined, "There's no `paymentDataRef.current` on payment authorization"]);
  }
  if (!shippingContact) {
    errorParamsArray.push(["shippingContactInvalid", undefined, "Shipping contact invalid"]);
  }
  if (shippingContact && !shippingContact.addressLines) {
    errorParamsArray.push(["shippingContactInvalid", "addressLines", "No shipping street and number given"]);
  }
  if (!billingContact) {
    errorParamsArray.push(["billingContactInvalid", undefined, "Billing contact invalid"]);
  }
  if (billingContact && !billingContact.addressLines) {
    errorParamsArray.push(["billingContactInvalid", "addressLines", "Billing address incomplete"]);
  }

  return errorParamsArray;
};

export const setAPayDataOnViewState = (nonce: string, context: ICartContext, place: PlaceType) => {
  context.setViewState({
    ...context.viewState,
    appleData: {nonce, paymentSource: "applepay", place},
    gPayData: undefined,
    paypalInfos: undefined,
  });
};

export function appleAddressAdapter(contact: ApplePayPaymentContact): IAddressData {
  const [street, streetNumber] = splitStreetAndNumber((contact?.addressLines ?? [""])[0], contact?.countryCode ?? "");
  const info = `Address line '${
    (contact.addressLines ?? ["[empty]"])[0]
  }' has been split into street: '${street}' and number: '${streetNumber}'`;
  if (!street || !streetNumber) {
    const e = new Error("Street name or house number could not be determined");
    e.name = "WrongAddressError";
    Sentry.setExtra("splitStreetAndNumber", info);
    throw e;
  }

  return {
    firstName: contact.givenName ?? "",
    lastName: contact.familyName ?? "",
    zip: contact.postalCode ?? "",
    city: contact.locality ?? "",
    countryCode: contact.countryCode ?? "",
    state: contact.administrativeArea ?? "",
    phone: contact.phoneNumber ?? "",
    street,
    number: streetNumber,
    company: null,
    department: null,
    gender: null,
    title: null,
    dateOfBirth: null,
    email: contact.emailAddress ?? "",
  };
}

export function positionsToLineItems(cart: Cart | ICartData): ApplePayLineItem[] {
  return positionsToDisplayItems(cart).map(
    (pos: google.payments.api.DisplayItem): ApplePayLineItem => ({label: pos.label, amount: pos.price}),
  );
}

const errorUpdate = (cart: Cart): ApplePayShippingContactUpdate => ({
  errors: [
    new ApplePayError("addressUnserviceable", "countryCode", i18n.t("checkout.noShippingToSelectedAddress")),
  ],
  newTotal: {
    label: i18n.t("cart.total"),
    amount: cart.valuePayable.amount,
  },
  newLineItems: positionsToLineItems(cart),
  newShippingMethods: [],
});

type ProductDataType = {lineItems: ApplePayJS.ApplePayLineItem[]; totalAmount: string};

export const getProductData = (): null | ProductDataType => {
  // item price
  const priceValue = dbqs<HTMLSpanElement>("span.price-value[data-price-value]")?.priceValue;
  if (!priceValue) return null;
  let totalAmount = parseFloat(priceValue);

  // Temporary, can be deleted after first VP
  const dlTitle = window.dataLayer.find((dl) => !!dl["Page.ItemName"]) || {"Page.ItemName": "Product"};

  // item label
  const label =
    dbqs<HTMLDivElement>("div.product-details[data-product-name]")?.productName || (dlTitle["Page.ItemName"] as string);

  // product line item
  const lineItems = [
    {
      label,
      amount: priceValue,
    },
  ];

  // shipping cost by dataset attribute
  const shippingCostString = dbqs<HTMLSpanElement>(
    "span.addnote.product-details__shipping a[data-shipping-cost]",
  )?.shippingCost;
  if (!shippingCostString) return null;

  // shipping name, retrieved from the live-stock-transport-time-*.twig
  const shippingName =
    dbqs<HTMLAnchorElement>(".product-details__shipping a[data-shipping-name]")?.shippingName || "Shipping";

  // shipping cost by parse float
  const shippingCost = parseFloat(shippingCostString);
  if (shippingCost !== 0.0) {
    // push shipping on the lineItems
    lineItems.push({
      label: shippingName,
      amount: shippingCostString,
    });

    // if not free, add to total amount
    totalAmount += shippingCost;
    totalAmount = Math.round(totalAmount * 100) / 100;
  }

  return {lineItems, totalAmount: String(totalAmount)};
};

export function dbqs<T extends HTMLElement>(selector: string) {
  return document.body.querySelector<T>(selector)?.dataset;
}
